/**
 * FRIDGE-1615 — what the stall signal reads about Supabase reads on one instance, shared by every copy of the read
 * code in the process.
 *
 * Next bundles a module into each server bundle that imports it, so a module-level counter would count only its own
 * bundle's reads. The gauge therefore lives on `globalThis` under one registered symbol, the way
 * lib/fail-fast-render.ts keeps its render scope, and every copy of lib/supabase/deadline-fetch.ts and
 * lib/supabase-read.ts updates the same object.
 *
 * This file is isomorphic on purpose: the deadline fetch also runs in the browser, so nothing here imports a Node
 * module. The server-only reporter (lib/observability/page-read-stall.ts) installs `onDeadline` from instrumentation;
 * where it is not installed (the browser, scripts, tests) a deadline only fails its read, exactly as before.
 *
 * Cost on a read that finishes: four integer updates on this object (two around the wait for headers, two around the
 * body read). Nothing is sampled until a deadline fires.
 */

/** Before headers, or while the response body is read. */
export type ReadDeadlinePhase = 'headers' | 'body'

/** Called once when a read's deadline fires. Must not throw; the caller guards it anyway. */
export type ReadDeadlineHook = (phase: ReadDeadlinePhase, url: string) => void

/** One shared read pool's load (lib/supabase-read.ts): reads holding a slot and reads queued for one. */
export interface ReadPoolLoad {
  active: number
  waiting: number
}

export interface ReadGauge {
  /** Site-client reads sent and still waiting for response headers. */
  awaitingResponse: number
  /** Site-client reads whose response body is being read. */
  readingBody: number
  /** One probe per copy of lib/supabase-read.ts in the process; read only when a deadline fires. */
  readonly pools: Set<() => ReadPoolLoad>
  /** The stall reporter, when installed. */
  onDeadline: ReadDeadlineHook | null
}

const GAUGE_KEY = Symbol.for('fridge.supabaseReadGauge')

type GlobalWithGauge = typeof globalThis & { [GAUGE_KEY]?: ReadGauge }

export function readGauge(): ReadGauge {
  const g = globalThis as GlobalWithGauge
  g[GAUGE_KEY] ??= { awaitingResponse: 0, readingBody: 0, pools: new Set(), onDeadline: null }
  return g[GAUGE_KEY]
}

/** Site-client reads in flight on this instance: sent and not yet answered, or answered with a body still being read. */
export function siteClientReadsInFlight(): number {
  const gauge = readGauge()
  return gauge.awaitingResponse + gauge.readingBody
}

/** The shared read pools' load summed across every copy in the process. */
export function readPoolLoad(): ReadPoolLoad {
  let active = 0
  let waiting = 0
  for (const probe of readGauge().pools) {
    const load = probe()
    active += load.active
    waiting += load.waiting
  }
  return { active, waiting }
}
